import { Button, Card, Form, ModalFooter, Page, RamblaActionButtonContent } from 'components';
import ConditionalSpinner from 'components/ConditionalSpinner/ConditionalSpinner';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toastr } from 'utils';
import { useCreateWidget } from 'api/widgets/hooks';
import { WIDGETS } from 'constants/routes';
import WIDGET_ICONS from './constants/widgetIcons';
import WIDGET_TYPES from './widgets';
import * as Ui from './WidgetsPage.styles';

const WidgetCreatePage = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'widgets' });
  const { t: tApp } = useTranslation('app');
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [widgetTypeKey, setWidgetTypeKey] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [pendingTypeKey, setPendingTypeKey] = useState(null);
  const { mutate: createWidget, isPending } = useCreateWidget();

  const activeWidget = widgetTypeKey ? WIDGET_TYPES[widgetTypeKey] : null;

  const handleOpenTypeModal = () => {
    setPendingTypeKey(widgetTypeKey);
    setIsTypeModalOpen(true);
  };

  const handleConfirmType = () => {
    if (pendingTypeKey !== widgetTypeKey) {
      const newWidget = WIDGET_TYPES[pendingTypeKey];
      if (newWidget?.defaultValues && formRef.current) {
        Object.entries(newWidget.defaultValues).forEach(([key, value]) => {
          formRef.current.setFieldValue(key, value);
        });
      }
    }
    setWidgetTypeKey(pendingTypeKey);
    setIsTypeModalOpen(false);
  };

  const handleCloseTypeModal = () => {
    setIsTypeModalOpen(false);
    setPendingTypeKey(null);
  };

  const handleSubmit = useCallback(() => {
    if (!formRef.current || !activeWidget) return;
    const { values } = formRef.current;
    if (!values.name?.trim() || !activeWidget.isValid(values)) return;

    createWidget(
      {
        name: values.name,
        type: widgetTypeKey,
        ...activeWidget.buildPayload(values),
      },
      {
        onSuccess: (data) => {
          toastr.success(t('toastr:success'), t('createSuccess'));
          navigate(`/widgets/${data.uid}`);
        },
      },
    );
  }, [activeWidget, widgetTypeKey, createWidget, navigate, t]);

  const ConfigComponent = activeWidget?.ConfigComponent;
  const isFormValid = !!formValues.name?.trim() && activeWidget?.isValid(formValues);
  const ActiveIcon = widgetTypeKey ? WIDGET_ICONS[widgetTypeKey] : null;

  return (
    <Page
      title={t('title')}
      breadcrumbs={[{ label: tApp('breadcrumbs.widgets'), to: WIDGETS }, { label: t('create') }]}
      description={t('description')}
      withBackButton={false}
      footer={{
        component: (
          <Ui.FooterWrapper>
            <Button
              size="lg"
              variant="dark"
              className="rounded"
              onClick={handleSubmit}
              disabled={!isFormValid || isPending}
            >
              <ConditionalSpinner showSpinner={isPending}>
                <RamblaActionButtonContent icon="addModule">
                  {t('create')}
                </RamblaActionButtonContent>
              </ConditionalSpinner>
            </Button>
          </Ui.FooterWrapper>
        ),
        visible: !!activeWidget,
      }}
    >
      <Form initialValues={{ name: '' }} onSubmit={() => {}} renderDefaultButtons={false}>
        {(formikProps) => {
          formRef.current = formikProps;
          setFormValues(formikProps.values);
          return (
            <Ui.ContentWrapper>
              <Card
                title={t('widgetDetails')}
                subtitle={t('widgetDetailsSubtitle')}
                className="w-100"
                style={{ marginTop: 24 }}
              >
                <Form.GroupHorizontal label={t('widgetName')} required>
                  <Form.Input
                    name="name"
                    placeholder={t('widgetNamePlaceholder')}
                    validate={(value) => (!value?.trim() ? tApp('required') : undefined)}
                  />
                </Form.GroupHorizontal>
                <Form.GroupHorizontal label={t('widgetType')} required style={{ marginTop: 16 }}>
                  <Ui.TypeSelectorCard
                    role="button"
                    tabIndex={0}
                    onClick={handleOpenTypeModal}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpenTypeModal();
                      }
                    }}
                  >
                    {activeWidget ? (
                      <>
                        <Ui.TypeSelectorIcon>
                          {ActiveIcon && <ActiveIcon size={28} />}
                        </Ui.TypeSelectorIcon>
                        <Ui.TypeSelectorInfo>
                          <Ui.TypeSelectorLabel>{t(activeWidget.titleKey)}</Ui.TypeSelectorLabel>
                          <Ui.TypeSelectorDescription>
                            {t(activeWidget.descriptionKey)}
                          </Ui.TypeSelectorDescription>
                        </Ui.TypeSelectorInfo>
                        <Ui.TypeSelectorChangeHint>{t('changeType')}</Ui.TypeSelectorChangeHint>
                      </>
                    ) : (
                      <Ui.TypeSelectorPlaceholder>
                        {t('selectWidgetType')}
                      </Ui.TypeSelectorPlaceholder>
                    )}
                  </Ui.TypeSelectorCard>
                </Form.GroupHorizontal>
              </Card>

              {ConfigComponent && <ConfigComponent />}
            </Ui.ContentWrapper>
          );
        }}
      </Form>

      <Ui.TypeSelectModal
        open={isTypeModalOpen}
        onClose={handleCloseTypeModal}
        header={{ title: t('selectWidgetType') }}
        footer={() => (
          <ModalFooter
            onCancel={handleCloseTypeModal}
            onSave={handleConfirmType}
            cancelText={tApp('cancel')}
            saveText={tApp('confirm')}
            isSaveDisabled={!pendingTypeKey}
          />
        )}
      >
        <Ui.TypeSelectModalBody>
          {Object.entries(WIDGET_TYPES).map(([key, widget]) => {
            const Icon = WIDGET_ICONS[key];
            return (
              <Ui.TypeCard
                key={key}
                role="button"
                tabIndex={0}
                $selected={pendingTypeKey === key}
                onClick={() => setPendingTypeKey(key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPendingTypeKey(key);
                  }
                }}
              >
                <Ui.TypeCardIcon>{Icon && <Icon size={28} />}</Ui.TypeCardIcon>
                <Ui.TypeCardInfo>
                  <Ui.TypeCardLabel>{t(widget.titleKey)}</Ui.TypeCardLabel>
                  <Ui.TypeCardDescription>{t(widget.descriptionKey)}</Ui.TypeCardDescription>
                </Ui.TypeCardInfo>
              </Ui.TypeCard>
            );
          })}
        </Ui.TypeSelectModalBody>
      </Ui.TypeSelectModal>
    </Page>
  );
};

export default WidgetCreatePage;
